import { loadClientsNonAdmin } from "@/core/clients/loadClientsNonAdmin";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/hooks/auth/useAuth";
import { useMemo } from "react";

export const useNonAdminClients = () => {
  const { role, currentUser } = useAuth();
  if (!currentUser || !role) {
    throw new Error("No authenticated user");
  }

  const {
    data: clients,
    isLoading: isLoadingClients,
    error,
  } = useQuery({
    queryKey: ["clients", currentUser?.uid, role],
    refetchInterval: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnMount: true,
    enabled: !!currentUser && !!role,
    staleTime: 1000 * 60 * 10, // 10 minutes
    queryFn: () => {
      if (!currentUser?.uid || !role) {
        throw new Error("User info is required");
      }
      return loadClientsNonAdmin(role, currentUser?.uid);
    },
  });

  const getClientDetails = (clientId: string) => {
    const client = clients?.find((client) => client.id === clientId);
    return client;
  };

  const accessibleClientIds = useMemo(() => {
    const ids = clients?.map((client) => client.id) || [];
    return ids;
  }, [clients, isLoadingClients]);

  return {
    clients,
    isLoadingClients,
    clientsError: error,
    getClientDetails,
    accessibleClientIds,
  };
};
